import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SymbolType } from '@op/constants/chart';
import { EventDetailProps, useDetailEvent } from '../hooks/useDetailEvent';
import _, { isEqual } from 'lodash-es';

export type ChangeDetailPageType = (
  value: any,
  type?: 'props' | 'config' | 'filter',
) => void;

// 定义由高阶组件注入的属性类型
export type ChartEventInjectedProps = {
  filter?: any;
  chartType?: SymbolType;
  setChartType?: (chartType: SymbolType) => void;
  eventsDict?: any;
  getEventsDict?: (chartType: SymbolType) => any;
  fullTableWrapper?: any;
  goPage?: (params: any) => void;
  changeHasDetailPage?: (goDetail: boolean) => void;
  onChartReady?: (chart: any) => void;
  changeDetailPage?: ChangeDetailPageType;
};

export const ChartEventContext = React.createContext<
  Omit<ChartEventInjectedProps, 'chartType' | 'setChartType' | 'filter'>
>({});

export default function chartEvent<T extends Record<string, any>>(
  eventProps: Omit<
    EventDetailProps,
    | 'chartType'
    | 'filter'
    | 'hasDetailPage'
    | 'defaultConfig'
    | 'defaultProps'
    | 'componentProps'
  > & {
    defaultChartType: SymbolType;
    hasDetailPage?: boolean;
    config?: any;
    props?: any;
    fixedFilter?: any;
  },
) {
  return (Component: React.ComponentType<T & ChartEventInjectedProps>) => {
    return function ChartWrapper(
      componentProps: Omit<T, keyof ChartEventInjectedProps>,
    ) {
      const { isDetail, updateDetailFilter, filter } = componentProps;
      const {
        defaultChartType,
        hasDetailPage: hasDetailPageProps,
        config,
        props,
        fixedFilter,
        ...otherEventProps
      } = eventProps;
      const [chartType, setChartType] = useState<SymbolType>(defaultChartType);
      const realFilter = useRef(filter);
      const defProps = useRef(props);
      const defConfig = useRef(config);
      const componentPropsRef = useRef(componentProps);

      useEffect(() => {
        componentPropsRef.current = componentProps;
      }, [
        componentProps,
        (prev: Omit<T, keyof ChartEventInjectedProps>) =>
          isEqual(prev, componentProps),
      ]);

      useEffect(() => {
        realFilter.current = { ...filter, ...fixedFilter };
      }, [filter]);

      const changeDetailPage = useCallback(
        (value: any, type: 'props' | 'config' | 'filter' = 'filter') => {
          if (type === 'props') {
            defProps.current = { ...defProps.current, ...value };
          } else if (type === 'config') {
            defConfig.current = { ...defConfig.current, ...value };
          } else {
            realFilter.current = {
              ...realFilter.current,
              ...fixedFilter,
              ...value,
            };
          }
        },
        [],
      );

      const hasDetailPageRef = useRef<boolean>(hasDetailPageProps || true);
      const changeHasDetailPage = useCallback((flag: boolean) => {
        hasDetailPageRef.current = flag;
      }, []);

      const {
        eventsDict,
        goPage,
        getEventsDict,
        fullTableWrapper,
        onChartReady,
      } = useDetailEvent({
        ...otherEventProps,
        chartType,
        isDetail,
        updateDetailFilter,
        filter: realFilter,
        hasDetailPage: hasDetailPageRef,
        defaultConfig: defConfig,
        defaultProps: defProps,
        componentProps: componentPropsRef,
      });

      const contextValue = useMemo(() => {
        return {
          eventsDict,
          goPage,
          getEventsDict,
          fullTableWrapper,
          onChartReady,
          changeDetailPage,
          changeHasDetailPage,
        };
      }, [
        eventsDict,
        goPage,
        getEventsDict,
        fullTableWrapper,
        onChartReady,
        changeDetailPage,
        changeHasDetailPage,
      ]);

      // 使用类型断言确保类型兼容
      return (
        <ChartEventContext.Provider value={contextValue}>
          <Component
            {...(componentProps as T)}
            eventsDict={eventsDict}
            fullTableWrapper={fullTableWrapper}
            getEventsDict={getEventsDict}
            setChartType={setChartType}
            chartType={chartType}
            goPage={goPage}
            changeHasDetailPage={changeHasDetailPage}
            onChartReady={onChartReady}
            changeDetailPage={changeDetailPage}
          />
        </ChartEventContext.Provider>
      );
    };
  };
}
